import { useQueryClient } from "@tanstack/react-query";
import { $api } from "../generated/client";

const CLIENTS_PATH = "/super-admin/clients";
const CLIENT_PATH = "/super-admin/clients/{clientId}";

/**
 * Lists, the client cards, the shop overview (client columns) and the shops'
 * configuration all show the grouping: refresh them after any client write.
 */
export function useInvalidateClients({
  isClientCardIncluded = true,
}: { isClientCardIncluded?: boolean } = {}) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all(
      [
        CLIENTS_PATH,
        ...(isClientCardIncluded ? [CLIENT_PATH] : []),
        "/super-admin/shops",
        "/super-admin/shops/{shopId}/configuration",
      ].map((path) =>
        queryClient.invalidateQueries({ queryKey: ["get", path] }),
      ),
    );
}

/** Baback clients, by name, with their shops (BBS-406). */
export function useSuperAdminClients() {
  return $api.useQuery("get", CLIENTS_PATH);
}

export function useSuperAdminClient(clientId: string) {
  return $api.useQuery("get", CLIENT_PATH, {
    params: { path: { clientId } },
  });
}

export function useCreateSuperAdminClient() {
  const invalidate = useInvalidateClients();
  return $api.useMutation("post", CLIENTS_PATH, { onSuccess: invalidate });
}

/**
 * The fleet overview and the shops' configuration only show the client's
 * name, status and CSM: refetched only when one of them changed.
 */
export function useUpdateSuperAdminClient() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateClients();
  return $api.useMutation("patch", CLIENT_PATH, {
    onSuccess: (_, { body }) => {
      const isShownOnShops = ["name", "status", "csm"].some(
        (field) => field in body,
      );
      if (isShownOnShops) return invalidate();
      return Promise.all(
        [CLIENTS_PATH, CLIENT_PATH].map((path) =>
          queryClient.invalidateQueries({ queryKey: ["get", path] }),
        ),
      );
    },
  });
}

/** The client in the path absorbs `sourceClientId`, which is deleted. */
export function useMergeSuperAdminClients() {
  const invalidate = useInvalidateClients();
  return $api.useMutation("post", "/super-admin/clients/{clientId}/merge", {
    onSuccess: invalidate,
  });
}

/** The deleted client's card is dropped from the cache, never refetched. */
export function useDeleteSuperAdminClient(clientId: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateClients({ isClientCardIncluded: false });
  return $api.useMutation("delete", CLIENT_PATH, {
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: ["get", CLIENT_PATH, { params: { path: { clientId } } }],
      });
      return invalidate();
    },
  });
}

/** Attaches a shop to a client (`null` detaches), edits its internal fields. */
export function useUpdateSuperAdminShopAccount() {
  const invalidate = useInvalidateClients();
  return $api.useMutation(
    "patch",
    "/super-admin/shops/{shopId}/configuration/account",
    { onSuccess: invalidate },
  );
}

/**
 * Attaches up to 100 shops to a client, or detaches them (`null`), in one
 * request: the bulk action of the shop list.
 */
export function useSetSuperAdminShopsClient() {
  const invalidate = useInvalidateClients();
  return $api.useMutation("put", "/super-admin/clients/shop-links", {
    onSuccess: invalidate,
  });
}
